import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { from } from "rxjs";
import { catchError, exhaustMap, map, mergeMap, tap } from "rxjs/operators";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { ServiceModeService } from "src/app/shared/services/service-modes/service-modes.service";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";
import { AppState } from "../../reducers";

import * as ServiceModeActionTypes from "../../actions/services-modes/services-mode.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";

@Injectable()
export class ServiceModeEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private serviceModeService: ServiceModeService,
    private tableConfigService: TableConfigService,
    private translate: TranslateService
  ) {}

  getServiceModes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceModeActionTypes.getServiceModes),
      exhaustMap((action) =>
        this.serviceModeService
          .getServiceModes(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              ServiceModeActionTypes.loadServiceModes({ response: result })
            ),
            catchError((error) => {
              const actions = [
                ServiceModeActionTypes.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "ServiceMode.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );
  getExcelServiceModes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceModeActionTypes.getServiceModesExcel),
      exhaustMap((action) =>
        this.serviceModeService
          .getServiceModes(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              ServiceModeActionTypes.loadServiceModesExcel({ response: result })
            ),
            catchError((error) => {
              const actions = [
                ServiceModeActionTypes.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "ServiceMode.loadExcelError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );
  getServiceMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceModeActionTypes.getServiceMode),
      exhaustMap((action) =>
        this.serviceModeService.getServiceMode(action.code).pipe(
          map((result) =>
            ServiceModeActionTypes.loadServiceMode({ serviceMode: result })
          ),
          catchError((error) => {
            const actions = [
              ServiceModeActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "ServiceMode.loadServiceModeError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createServiceMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceModeActionTypes.createServiceMode),
      exhaustMap((action) =>
        this.serviceModeService.createServiceMode(action).pipe(
          mergeMap((result) => [
            ServiceModeActionTypes.resetCreation(),
            ServiceModeActionTypes.getServiceMode({
              code: result?.result?.code,
            }),
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "ServiceMode.serviceCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              ServiceModeActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "ServiceMode.serviceCreateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
  updateServiceMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceModeActionTypes.updateServiceMode),
      exhaustMap((action) =>
        this.serviceModeService.updateServiceMode(action.serviceMode).pipe(
          map((result) => {
            return ServiceModeActionTypes.getServiceMode({
              code: result?.result?.code,
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "ServiceMode.serviceModeUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              ServiceModeActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "ServiceMode.serviceModeUpdatedFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
  // Get Service Mode Table Config
  getServiceModeTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceModeActionTypes.getServiceModeTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(action.options.code, action.options.settingType)
          .pipe(
            map((result) =>
              ServiceModeActionTypes.loadServiceModeTableConfig({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                ServiceModeActionTypes.loadServiceModeTableConfig({
                  response: [],
                }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Service Mode Table Config
  saveServiceModeTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceModeActionTypes.saveServiceModeTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
            ServiceModeActionTypes.setLoadingEndState(),
            MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj(
                "TableConfig.SaveSuccess",
                SnackbarType.SUCCESS
              )
            ),
          ]),
          catchError((error: any) => {
            const actions = [
              ServiceModeActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  buildSnackbarObj(message: string, snackbarType: SnackbarType) {
    return {
      snackbar: {
        message: this.translate.instant(message),
        type: snackbarType,
      },
    };
  }
}
